import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { submitLead } from "../../lib/api";
import { Field, inputCls, Honeypot } from "./fields";
import { CTAButton } from "../common/CTAButton";
import { industryOptions, employmentTypes } from "../../data/content";

const schema = z.object({
    full_name: z.string().min(2, "Enter your full name"),
    company_name: z.string().min(2, "Enter company name"),
    email: z.string().email("Enter a valid official email"),
    mobile: z.string().min(8, "Enter a valid mobile number"),
    industry: z.string().optional(),
    role: z.string().optional(),
    manpower_count: z.string().optional(),
    location: z.string().optional(),
    employment_type: z.string().optional(),
    joining_date: z.string().optional(),
    additional: z.string().optional(),
    honeypot: z.string().optional(),
});

export const ManpowerForm = () => {
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

    const onSubmit = async (values) => {
        try {
            await submitLead("manpower", values);
            toast.success("Requirement submitted. Our team will contact you shortly.");
            reset();
        } catch (e) {
            toast.error("Something went wrong. Please try again or contact our team.");
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} data-testid="manpower-form" className="space-y-5">
            <Honeypot register={register} />
            <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" required error={errors.full_name?.message}>
                    <input className={inputCls} placeholder="Your name" data-testid="mp-full-name" {...register("full_name")} />
                </Field>
                <Field label="Company Name" required error={errors.company_name?.message}>
                    <input className={inputCls} placeholder="Company name" data-testid="mp-company" {...register("company_name")} />
                </Field>
                <Field label="Official Email" required error={errors.email?.message}>
                    <input className={inputCls} placeholder="name@company.com" data-testid="mp-email" {...register("email")} />
                </Field>
                <Field label="Mobile Number" required error={errors.mobile?.message}>
                    <input className={inputCls} placeholder="+91" data-testid="mp-mobile" {...register("mobile")} />
                </Field>
                <Field label="Industry">
                    <select className={inputCls} data-testid="mp-industry" defaultValue="" {...register("industry")}>
                        <option value="" disabled>Select industry</option>
                        {industryOptions.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                </Field>
                <Field label="Required Role">
                    <input className={inputCls} placeholder="e.g. Site Supervisors" data-testid="mp-role" {...register("role")} />
                </Field>
                <Field label="Number of Manpower Required">
                    <input className={inputCls} placeholder="e.g. 50" data-testid="mp-count" {...register("manpower_count")} />
                </Field>
                <Field label="Location">
                    <input className={inputCls} placeholder="City / State" data-testid="mp-location" {...register("location")} />
                </Field>
                <Field label="Employment Type">
                    <select className={inputCls} data-testid="mp-type" defaultValue="" {...register("employment_type")}>
                        <option value="" disabled>Select type</option>
                        {employmentTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
                <Field label="Expected Joining Date">
                    <input type="date" className={inputCls} data-testid="mp-date" {...register("joining_date")} />
                </Field>
            </div>
            <Field label="Additional Requirement">
                <textarea rows={4} className={inputCls} placeholder="Tell us more about your requirement" data-testid="mp-additional" {...register("additional")} />
            </Field>
            <CTAButton type="submit" variant="primary" arrow disabled={isSubmitting} data-testid="mp-submit" className="w-full sm:w-auto disabled:opacity-60">
                {isSubmitting ? "Submitting..." : "Submit Requirement"}
            </CTAButton>
        </form>
    );
};
